import React, { forwardRef, useMemo, useCallback } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useFormField } from '../FormField/FormField';

/**
 * DateTimeInput — input date + heure avec raccourcis Bold Duotone.
 *
 *   <DateTimeInput
 *     value={date}
 *     onChange={setDate}
 *     min={new Date()}
 *     shortcuts
 *   />
 *
 * Props :
 *   - value     : Date | string ISO | null
 *   - onChange  : (Date | null) => void
 *   - min       : Date — date min autorisée (default = maintenant)
 *   - size      : 'sm' | 'md' | 'lg'
 *   - shortcuts : boolean — affiche la rangée de raccourcis (default true)
 *   - isInvalid, isDisabled, placeholder
 */

const SIZES = {
  sm: 'h-8  text-sm   px-2.5',
  md: 'h-10 text-sm   px-3',
  lg: 'h-11 text-base px-3.5',
};

function pad(n) { return String(n).padStart(2, '0'); }

/** Date → string format YYYY-MM-DDTHH:mm (pour datetime-local) */
function toInputValue(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function buildShortcuts(now = new Date()) {
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);

  const tomorrow9 = new Date(now);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);

  // Prochain lundi à 9h (si on est lundi, on prend lundi prochain)
  const nextMonday9 = new Date(now);
  nextMonday9.setHours(9, 0, 0, 0);
  const day = nextMonday9.getDay(); // 0 = dimanche, 1 = lundi
  const daysUntilMonday = day === 1 ? 7 : ((1 - day + 7) % 7) || 7;
  nextMonday9.setDate(nextMonday9.getDate() + daysUntilMonday);

  // Vendredi 18h (fin de semaine type)
  const nextFriday18 = new Date(now);
  nextFriday18.setHours(18, 0, 0, 0);
  const dayF = nextFriday18.getDay();
  const daysUntilFriday = dayF === 5 ? (now > nextFriday18 ? 7 : 0) : ((5 - dayF + 7) % 7);
  nextFriday18.setDate(nextFriday18.getDate() + (daysUntilFriday === 0 && now > nextFriday18 ? 7 : daysUntilFriday));

  return [
    { label: 'Dans 1h',         value: inOneHour },
    { label: 'Demain 9h',       value: tomorrow9 },
    { label: 'Lundi prochain',  value: nextMonday9 },
    { label: 'Vendredi 18h',    value: nextFriday18 },
  ];
}

const FORMATTER = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const DateTimeInput = forwardRef(function DateTimeInput(
  {
    value,
    onChange,
    min,
    size = 'md',
    shortcuts = true,
    isInvalid,
    isDisabled,
    placeholder,
    className,
    id: idProp,
    ...rest
  },
  ref,
) {
  const field = useFormField();
  const id = idProp || field?.id;
  const invalid = isInvalid || field?.invalid;

  const minValue = useMemo(() => toInputValue(min || new Date()), [min]);
  const inputValue = useMemo(() => toInputValue(value), [value]);

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    if (!v) return onChange?.(null);
    const d = new Date(v);
    if (isNaN(d.getTime())) return;
    onChange?.(d);
  }, [onChange]);

  const shortcutsList = useMemo(() => buildShortcuts(), []);

  const parsedPreview = useMemo(() => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return null;
    return FORMATTER.format(d);
  }, [value]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="relative">
        <Calendar
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mute"
          strokeWidth={1.8}
        />
        <input
          ref={ref}
          id={id}
          type="datetime-local"
          value={inputValue}
          onChange={handleChange}
          min={minValue}
          disabled={isDisabled}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          aria-describedby={field?.describedBy}
          className={cn(
            'w-full appearance-none bg-surface text-text outline-none pl-9',
            'border border-border rounded-md transition-colors duration-fast',
            'focus:bg-surface focus:border-ink focus:ring-2 focus:ring-ink/15',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-2',
            SIZES[size],
            invalid && 'border-danger focus:border-danger focus:ring-danger/20',
          )}
          {...rest}
        />
      </div>

      {shortcuts && !isDisabled && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10.5px] font-mono uppercase tracking-wider text-mute mr-1">
            Raccourcis
          </span>
          {shortcutsList.map((s) => {
            const isActive = value && Math.abs(new Date(value).getTime() - s.value.getTime()) < 60000;
            return (
              <button
                key={s.label}
                type="button"
                onClick={() => onChange?.(s.value)}
                className={cn(
                  'inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[12px] font-medium transition-colors',
                  isActive
                    ? 'bg-ink text-cream'
                    : 'bg-cream-2 text-text hover:bg-cream-3 border border-cream-3',
                )}
              >
                <Clock className="w-3 h-3" strokeWidth={1.8} />
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      {parsedPreview && (
        <p className="text-[12px] text-mute font-mono">
          → <span className="text-text">{parsedPreview}</span>
        </p>
      )}
    </div>
  );
});

export default DateTimeInput;
